/**
 * ScheduleCard — 출하 1건을 카드 한 장으로 보여주는 공용 표시 컴포넌트.
 *
 * 원래 ScheduleTable 의 MO(≤659) 카드 리스트 마크업이었던 것을 추출했다.
 *  - 출하조회 MO 스케쥴  : 전체 목록
 *  - 홈 "금일 출하 내용" MO : 상위 2건만 + [상세내용 확인하기]
 *
 * 데이터 fetch·정렬은 호출측 책임. 여기서는 표시만 한다.
 * CSS 는 components.css 의 .dispatch-sched-card / .dsc-* 를 공유한다.
 */
import type { ReactNode } from "react";
import { classifyTier, type RqcShipment } from "@/domain/rqc";
import type { SiteMeta } from "@/domain/site_meta";
import { siteNameOf } from "@/utils/normalizeSiteName";
import { useShipmentMixPerm } from "@/hooks/useAuth";
import { displayCompany } from "@/utils/displayCompany";
import { fmt } from "./lib";
import { classifyStatus } from "./filterLogic";

/** 잔량 0 판정 오차 — filterLogic 의 EPS 와 같은 기준 */
const REMAIN_EPS = 0.05;

/** "0855" → "08:55", "1040" → "10:40". 비표준이면 원본 반환 */
export function fmtTime(s: string): string {
  if (!s) return "";
  const cleaned = String(s).replace(/[^\d]/g, "");
  if (cleaned.length === 4) return `${cleaned.slice(0, 2)}:${cleaned.slice(2)}`;
  return s;
}

interface Props {
  row: RqcShipment;
  /**
   * full — 출하조회 MO 스케쥴(기본): 현장 / 거래처 / 배합·물량 전부
   * mini — 홈 위젯 미리보기: 위젯 높이(2행 374px)에 2장이 들어가도록 거래처 줄을 접고
   *        관급·사급 칩을 현장명 줄로 올린다
   */
  variant?: "full" | "mini";
  /** 현장 표시명 lookup용 (`${scode}_${hcode}` → SiteMeta) */
  metas: Map<string, SiteMeta>;
  onClick?: (row: RqcShipment) => void;
  /** 탭/클릭 안내 툴팁 */
  title?: string;
  /** 수량 단위 (기본 ㎥, 경원 아스콘은 "톤") */
  unit?: string;
  /** 계획 수량 이름 (기본 "예정", 경원은 "수주") */
  qtyLabel?: string;
  /** 배합 칩 바꾸기(경원 규격 칩 여러 개). chipClass = 카드 칩 모양 */
  renderMix?: (r: RqcShipment, chipClass: string) => ReactNode;
  /** 예정(수주)·출하·잔량 숫자 표시 (기본 fmt 소수 첫째 자리) */
  fmtQty?: (v: number) => string;
}

export function ScheduleCard({ row: r, metas, onClick, title, variant = "full", unit = "㎥", qtyLabel = "예정", renderMix, fmtQty = fmt }: Props) {
  const mini = variant === "mini";
  // 4자리 배합 코드는 shipment.mix 있을 때만(규격 칩은 그대로). 화면 가림만 — §5-D
  const canMix = useShipmentMixPerm();
  const status = classifyStatus(r);
  const tier = classifyTier(r);
  const pct = r.qty > 0 ? Math.max(0, Math.min(100, (r.shipped / r.qty) * 100)) : 0;
  // 잔량은 카드 우상단 주 숫자 — 출하/예정을 머릿속으로 빼지 않아도 되게
  const remain = Number(r.remain) || 0;
  // 출하 0 이면 잔량이 0 이어도 완료 아님 — filterLogic.classifyStatus 와 같은 기준
  const isDone = r.qty > 0 && r.shipped > 0 && remain <= REMAIN_EPS;

  return (
    <div
      className={`dispatch-sched-card dispatch-row-${status} dispatch-row-${tier}${mini ? " is-mini" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onClick?.(r)}
      title={title}
    >
      <div className="dsc-top">
        <span className="dsc-time">
          <span className={`dispatch-row-dot dot-${status}`} />
          {fmtTime(r.firstcar) || "—"}
        </span>
        {r.qty > 0 ? (
          isDone
            ? <span className="dsc-remain is-done">출하 완료</span>
            : <span className="dsc-remain"><b>{fmtQty(remain)}</b>{unit} 남음</span>
        ) : (
          <span className="dsc-remain is-none">{qtyLabel} 없음</span>
        )}
        <span className="dsc-pct">{r.qty > 0 ? `${pct.toFixed(0)}%` : "—"}</span>
      </div>
      {mini ? (
        <div className="dsc-site-row">
          <span className={`dispatch-tier-chip is-${tier}`}>
            {tier === "gov" ? "관급" : "사급"}
          </span>
          <span className="dsc-site">{siteNameOf(r, metas) || "—"}</span>
        </div>
      ) : (
        <>
          <div className="dsc-site">{siteNameOf(r, metas) || "—"}</div>
          <div className="dsc-sub">
            <span className={`dispatch-tier-chip is-${tier}`}>
              {tier === "gov" ? "관급" : "사급"}
            </span>
            <span className="dsc-company">{displayCompany(r.company) || "—"}</span>
          </div>
        </>
      )}
      <div className="dsc-figures">
        {renderMix ? renderMix(r, "dsc-mix-chip") : <span className="dsc-mix-chip">{r.mix || "—"}</span>}
        {canMix && r.code ? <span className="dsc-code-mini">{r.code}</span> : null}
        <span className="dsc-qty">
          <b>{fmtQty(r.shipped)}</b>
          <span className="dsc-qty-sep">/</span>
          {fmtQty(r.qty)}
          <span className="dsc-qty-unit">{unit}</span>
        </span>
        {r.cumcars > 0 ? <span className="dsc-cumcars">{fmt(r.cumcars, 0)}탕</span> : null}
      </div>
      {r.qty > 0 && (
        <div className="dsc-bar">
          <div className="dsc-bar-fill" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
